import { useEffect, useId, useRef } from "react"
import { useIsMobile } from "@/hooks/use-mobile"
import { useDocument } from "@/lib/queries"
import { DocumentViewer } from "@/components/DocumentViewer"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Loader2, X } from "lucide-react"

export function ClinicalReviewDrawer({
  slug,
  title,
  open,
  onOpenChange,
}: {
  slug: string
  title: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const isMobile = useIsMobile()
  const headingId = useId()

  // Lazy but not re-fetch-on-every-toggle: before the first open, querySlug is
  // "" so useDocument's `enabled: !!slug` keeps it disabled and nothing is
  // downloaded. On first open `open` flips the slug in immediately; the ref
  // (set in the effect below) then keeps the query key at `slug` for the rest
  // of this mount, so closing and reopening reuses the cached query instead
  // of toggling the key between "" and slug and forcing a fresh fetch.
  const hasOpenedRef = useRef(false)
  useEffect(() => {
    if (open) hasOpenedRef.current = true
  }, [open])
  const querySlug = open || hasOpenedRef.current ? slug : ""

  const { data: document, isLoading } = useDocument(querySlug)

  // A document already in hand beats a failed refetch: a stale copy of a
  // clinical review is still useful reference material, and a transient
  // network error should not make previously loaded content disappear.
  const body = isLoading ? (
    <div className="flex items-center justify-center p-8">
      <Loader2 className="mr-2 h-5 w-5 animate-spin text-muted-foreground" />
      <span className="text-sm text-muted-foreground">Loading clinical review…</span>
    </div>
  ) : document ? (
    // Every other DocumentViewer call site wraps it in `px-6 py-6`, and its
    // sticky title bar's `-mt-6 ... pt-6` is written to that contract. The
    // loading and error branches below carry their own padding.
    <div className="px-6 py-6">
      <DocumentViewer document={document} hideCitationsToggle />
    </div>
  ) : (
    <div className="p-6">
      <p className="text-sm text-destructive">
        This clinical review could not be loaded. You can still answer and submit
        your assessment.
      </p>
    </div>
  )

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full overflow-auto p-0 sm:max-w-md">
          <SheetHeader className="sticky top-0 z-10 border-b bg-background px-4 py-3 pr-10 text-left">
            <SheetTitle className="text-base">{title}</SheetTitle>
            <SheetDescription>Clinical review reference material</SheetDescription>
          </SheetHeader>
          {body}
        </SheetContent>
      </Sheet>
    )
  }

  if (!open) return null

  // Two thirds to the clinical review, one third to the questions. The review
  // carries its own Page Contents navigation, which needs the room: finding
  // clinical information is part of the competency being assessed, not just
  // knowing the answer.
  return (
    <aside
      aria-labelledby={headingId}
      className="flex w-2/3 shrink-0 flex-col border-l"
    >
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h2 id={headingId} className="text-base font-semibold">{title}</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onOpenChange(false)}
          aria-label="Close clinical review"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-auto">{body}</div>
    </aside>
  )
}
